/**
 * A table the composer wrote from the materials (DOP-533): market sizing by
 * segment, revenue streams with their shares, an order book, holdings with
 * carrying values. Every cited cell — figures, and the labels and text the
 * server found on the page (DOP-556) — is sourced like a sentence: hover
 * shows the page or cell, a click opens it. A cell kept without a source is
 * plain text: every figure in a kept row was verified before the table was
 * stored, so a digit in a label ("Vans RV14") is not a missing citation.
 * A cell a person typed in edit mode (DOP-637) has no source and reads in
 * the person's colour. The Financial Summary is NOT one of these: it is
 * rendered from the facts, never written.
 */

import React from "react";

import type {
	DealScreeningFact,
	DealScreeningSource,
	DealScreeningTable,
} from "../../types/dealScreening";
import MemoSentence from "./MemoSentence";
import PersonNote from "./PersonNote";

export interface MemoTableProps {
	table: DealScreeningTable;
	/** Stable prefix for the cells' ids: "<section>:<paragraph>". */
	idPrefix: string;
	factsFor: (keys: string[]) => DealScreeningFact[];
	onOpenSource: (source: DealScreeningSource) => void;
	/** Who edited the table and when, for a cell whose source is their note (DOP-637). */
	noteBy?: string | null;
	noteAt?: string | null;
}

const MemoTable: React.FC<MemoTableProps> = ({
	table,
	idPrefix,
	factsFor,
	onOpenSource,
	noteBy = null,
	noteAt = null,
}) => (
	<figure className="my-3" data-testid="memo-table">
		{table.caption && (
			<figcaption className="mb-2 font-sans text-[12px] font-semibold text-[#3f4247]">
				{table.caption}
			</figcaption>
		)}
		<div className="overflow-x-auto rounded-xl border border-[#e7e7e4]">
		<table className="w-full border-collapse font-sans text-[13.5px] leading-5 text-[#1F2937]">
			{table.columns.length > 0 && (
				<thead>
					<tr className="border-b border-[#e5e7eb] bg-[#fafafa]">
						{table.columns.map((column, index) => (
							<th
								// Columns are positional within an immutable table.
								// eslint-disable-next-line react/no-array-index-key
								key={index}
								scope="col"
								className={`px-4 py-2.5 text-xs font-semibold text-[#5B6472] ${
									index === 0 ? "text-left" : "text-right"
								}`}
							>
								{column}
							</th>
						))}
					</tr>
				</thead>
			)}
			<tbody>
				{table.rows.map((row, r) => (
					// eslint-disable-next-line react/no-array-index-key
					<tr key={r} className="border-b border-[#f1f2f4] last:border-0">
						{row.map((cell, c) => {
							const id = `${idPrefix}:${r}:${c}`;
							return (
								<td
									key={id}
									className={`px-4 py-2.5 align-top ${
										c === 0 ? "text-left" : "text-right tabular-nums"
									}`}
								>
									{cell.source?.kind === "user" ? (
										<PersonNote
											note={cell.source.note ?? null}
											by={noteBy}
											at={noteAt}
											align="right"
											testId="cell-note"
										>
											{cell.text}
										</PersonNote>
									) : cell.source ? (
										// A cell a person put in that names a document
										// (DOP-652) opens it, in the person's colour.
										<MemoSentence
											link="always"
											sentence={{ id, ...cell }}
											edited={Boolean(cell.edited)}
											facts={factsFor(cell.fact_keys)}
											onOpenSource={onOpenSource}
										/>
									) : cell.edited ? (
										<span
											className="text-[#5b3fa8]"
											title="Typed by a person; not from the files"
											data-testid="memo-table-edited-cell"
										>
											{cell.text}
										</span>
									) : cell.text.trim() ? (
										cell.text
									) : (
										// An empty cell reads as a dash, as the export prints it.
										<span className="text-[#9a9ea3]" aria-label="Empty">
											—
										</span>
									)}
								</td>
							);
						})}
					</tr>
				))}
			</tbody>
		</table>
		</div>
		{table.note && (
			<p className="mt-1 font-sans text-xs text-amber-800" data-testid="memo-table-note">
				{table.note}
			</p>
		)}
	</figure>
);

export default React.memo(MemoTable);
